/**
 * Stytch Authentication Helpers
 * 
 * Shared utilities for Stytch authentication integration:
 * - Session continuity helpers (localStorage persistence)
 * - Wizard state preservation during auth flows
 * - Redirect URL configuration
 * - Subdomain-aware navigation
 */

import { getAppUrl, isMarketingSite } from './subdomain';

// Session continuity: Wizard state persistence
export interface WizardSessionState {
  jobId?: number | string; // Job ID (can be number or string)
  jobSlug?: string; // SEO-friendly slug for redirect URL construction
  wizardSessionId?: string; // Database-backed wizard session ID for cross-subdomain persistence
  cvParsedData?: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    // Additional CV data fields
  };
  progressIndex?: number;
  timestamp: number;
}

const WIZARD_SESSION_KEY = 'talenture_wizard_session';
const SESSION_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Persist wizard session state to localStorage before Stytch redirect
 */
export function saveWizardSession(state: WizardSessionState): void {
  try {
    const payload = {
      ...state,
      timestamp: Date.now()
    };
    localStorage.setItem(WIZARD_SESSION_KEY, JSON.stringify(payload));
  } catch (error) {
    console.error('[Stytch Auth] Failed to save wizard session:', error);
  }
}

/**
 * Retrieve wizard session state from localStorage after Stytch auth
 * Returns null if expired or not found
 * Alias: restoreWizardSession
 */
export function getWizardSession(): WizardSessionState | null {
  try {
    const stored = localStorage.getItem(WIZARD_SESSION_KEY);
    if (!stored) return null;

    const state: WizardSessionState = JSON.parse(stored);
    
    // Check expiration
    const now = Date.now();
    if (now - state.timestamp > SESSION_EXPIRY_MS) {
      clearWizardSession();
      return null;
    }

    return state;
  } catch (error) {
    console.error('[Stytch Auth] Failed to retrieve wizard session:', error);
    return null;
  }
}

/**
 * Clear wizard session from localStorage (after successful completion or timeout)
 */
export function clearWizardSession(): void {
  try {
    localStorage.removeItem(WIZARD_SESSION_KEY);
  } catch (error) {
    console.error('[Stytch Auth] Failed to clear wizard session:', error);
  }
}

/**
 * Get the base URL for OAuth callbacks
 * In production, ALWAYS uses app.talenture.ai to ensure localStorage consistency
 * (OAuth redirect must return to the same origin where session was saved)
 */
function getOAuthBaseURL(): string {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // Development: use current origin
  if (hostname === 'localhost' || hostname.includes('.replit.dev') || hostname.includes('.repl.co')) {
    return window.location.origin;
  }
  
  // Production: always use app.talenture.ai
  // This ensures OAuth callbacks return to the same origin where localStorage was saved
  const parts = hostname.split('.');
  if (parts[0] === 'app') {
    return window.location.origin;
  }
  
  // On www or naked domain, construct app subdomain URL
  const baseDomain = parts[0] === 'www' ? parts.slice(1).join('.') : hostname;
  return `${protocol}//app.${baseDomain}`;
}

/**
 * Get Stytch redirect URLs for current environment
 * In production, callbacks ALWAYS go to app.talenture.ai (not www)
 */
export function getStytchRedirectURLs() {
  const baseURL = getOAuthBaseURL();
  
  return {
    candidate: {
      loginRedirectURL: `${baseURL}/auth/candidate/callback`,
      signupRedirectURL: `${baseURL}/auth/candidate/callback`,
    },
    company: {
      discoveryRedirectURL: `${baseURL}/auth/company/callback`,
    }
  };
}

/**
 * Detect if running on production domain
 * ONLY used for cookie configuration (sameSite, secure flags) in main.tsx
 * NOT used for token selection - tokens come from backend via /api/auth/stytch-config
 */
export function isProductionEnvironment(): boolean {
  return window.location.hostname.includes('talenture.ai');
}

/**
 * Get role-specific dashboard URL for post-auth redirect
 * Returns full URL (including app subdomain) when on marketing site in production
 */
export function getDashboardURL(role: 'candidate' | 'company' | 'admin'): string {
  let path: string;
  switch (role) {
    case 'candidate':
      path = '/candidate-dashboard';
      break;
    case 'company':
      path = '/company-dashboard';
      break;
    case 'admin':
      path = '/system/dashboard';
      break;
    default:
      path = '/';
  }
  
  // In production on marketing site, return full app subdomain URL
  return getAppUrl(path);
}

/**
 * Navigate to a URL, handling cross-subdomain redirects
 * Uses window.location.href for full URLs (cross-subdomain)
 * Uses setLocation callback for same-origin relative paths
 */
export function navigateToDashboard(
  role: 'candidate' | 'company' | 'admin',
  setLocation: (path: string) => void
): void {
  const url = getDashboardURL(role);
  
  // If it's a full URL (starts with http), use window.location for cross-subdomain navigation
  if (url.startsWith('http')) {
    window.location.href = url;
  } else {
    setLocation(url);
  }
}

// Alias for better semantic naming in wizard flow
export const restoreWizardSession = getWizardSession;

// Return URL persistence for deep linking
// Saves the URL user was trying to access before auth redirect
const RETURN_URL_KEY = 'talenture_return_url';
const RETURN_URL_EXPIRY_MS = 30 * 60 * 1000; // 30 minutes (same as wizard session)

interface ReturnUrlState {
  url: string;
  timestamp: number;
}

/**
 * Save return URL to localStorage before auth redirect
 * Used for deep linking - restores user to original destination after login
 * NOTE: Does NOT overwrite if a valid (unexpired) URL already exists
 */
export function saveReturnUrl(url: string): void {
  try {
    // Don't save auth-related URLs
    if (url.includes('/auth/') || url.includes('/callback')) {
      return;
    }
    
    // Check if a valid URL already exists - don't overwrite the original deep link
    const existing = localStorage.getItem(RETURN_URL_KEY);
    if (existing) {
      try {
        const existingState: ReturnUrlState = JSON.parse(existing);
        const now = Date.now();
        // If existing URL is still valid (not expired), don't overwrite
        if (now - existingState.timestamp <= RETURN_URL_EXPIRY_MS) {
          console.log('[Stytch Auth] Return URL already exists, skipping save:', existingState.url);
          return;
        }
      } catch {
        // Invalid JSON, continue to save new URL
      }
    }
    
    const payload: ReturnUrlState = {
      url,
      timestamp: Date.now()
    };
    localStorage.setItem(RETURN_URL_KEY, JSON.stringify(payload));
    console.log('[Stytch Auth] Saved return URL:', url);
  } catch (error) {
    console.error('[Stytch Auth] Failed to save return URL:', error);
  }
}

/**
 * Retrieve return URL from localStorage after auth
 * Returns null if expired or not found
 */
export function getReturnUrl(): string | null {
  try {
    const stored = localStorage.getItem(RETURN_URL_KEY);
    if (!stored) return null;

    const state: ReturnUrlState = JSON.parse(stored);
    
    // Check expiration
    const now = Date.now();
    if (now - state.timestamp > RETURN_URL_EXPIRY_MS) {
      clearReturnUrl();
      return null;
    }

    return state.url;
  } catch (error) {
    console.error('[Stytch Auth] Failed to retrieve return URL:', error);
    return null;
  }
}

/**
 * Clear return URL from localStorage (after use or expiration)
 */
export function clearReturnUrl(): void {
  try {
    localStorage.removeItem(RETURN_URL_KEY);
  } catch (error) {
    console.error('[Stytch Auth] Failed to clear return URL:', error);
  }
}
